import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIsMutating } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { PageStub } from '@/components/shared/PageStub';
import { isXeroEnabled } from '@/lib/feature-flags';
import { FinancialCockpit } from '@/components/tracking/FinancialCockpit';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { SetupOverlay } from '@/components/finance/SetupOverlay';
import { CloseOutDialog } from '@/components/finance/CloseOutDialog';
import { FinanceSkillCheck } from '@/components/finance/FinanceSkillCheck';
import { useCurrentUser } from '@/api/hooks';
import { useCurrentOrganization } from '@/hooks/use-bss-organizations';
import { XERO_MAPPING_WRITE_KEY, useSyncXeroProfitAndLoss } from '@/hooks/use-xero';
import type { LedgerAnswer } from '@/components/finance/LedgerTable';

/** What `?setup` puts on screen. Three states, not two — see `setupGate`. */
export type SetupGate = 'none' | 'ask' | 'ledger';

/**
 * Nothing, the skills check, or the mapping ledger.
 *
 * WHY THREE AND NOT A BOOLEAN. "Should I ask?" with the ledger as the default answer is the
 * obvious shape and it is wrong, because mounting the ledger is not free: `SetupOverlay`'s mount
 * effect fires a Xero pull (SetupOverlay.tsx:247) that is never cancelled and raises its own
 * error toast. So in the moment before the profile has loaded — a refresh, a pasted link — we
 * must render NEITHER, or the pull fires and the component is then torn out from under it when
 * the profile arrives with a null level.
 *
 * THE RULE: ask if and only if setup is requested, the profile has loaded, the level is EXACTLY
 * null, and nothing was answered this mount.
 *
 * `level` is passed through WITHOUT `?? null` on purpose. `null` is the column saying "never
 * asked"; `undefined` is the key not being there at all — an older cached profile, or the
 * self-heal stub in `getCurrentUser`. Re-asking on `undefined` would nag every builder whose
 * cache predates the column.
 */
export function setupGate(s: {
  setupRequested: boolean;
  profileSettled: boolean;
  level: number | null | undefined;
  answeredThisSession: boolean;
}): SetupGate {
  if (!s.setupRequested) return 'none';
  // SETTLED, not "loaded". The question is only ever "do we still expect to learn the level?",
  // and there are three ways the answer is no: the query succeeded, it errored (`retry: false`,
  // so one blip and it stays errored), or it succeeded with no row at all — `getCurrentUser`
  // returns null when there is no session. An earlier version keyed off "succeeded AND non-null"
  // and left the last two cases at 'none' forever: no overlay, and the cockpit behind it still
  // carries `inert`, so the whole screen is frozen with nothing explaining why.
  //
  // Each of those two cases then falls through as `level === undefined` and lands on 'ledger',
  // which is right — we are never going to learn their level, so get out of the way of the
  // mapping. 'none' is now the PENDING moment only, which is the one moment where mounting the
  // ledger would fire a Xero pull we would then tear out from under.
  if (!s.profileSettled) return 'none';
  if (s.level === null && !s.answeredThisSession) return 'ask';
  return 'ledger';
}

/**
 * /finance — the financial cockpit (F-124), fed by the Xero integration (F-013).
 *
 * Flag-gated with a conditional RENDER, not a redirect: the flag-off Xero routes
 * bounce here, so this page must always exist. Flag off (or unresolved) → the
 * original Finance Hub stub; on → the cockpit. The root beforeLoad warms the
 * flag cache, so the read is hot and synchronous.
 */
function FinancePage() {
  const { setup, chapter, account, role, metric, ask, closeout } = Route.useSearch();
  const navigate = Route.useNavigate();

  // Setup's staged answers and skipped chapters live HERE, not in the overlay, because the overlay
  // unmounts when it closes. Ruled 22 Sep: closing keeps unsaved answers in memory. A browser
  // refresh still drops them; the overlay's footer says "not saved" rather than implying otherwise.
  const [answers, setAnswers] = useState<Record<string, LedgerAnswer>>({});
  const [skipped, setSkipped] = useState<Set<string>>(new Set());

  // While setup is open the dashboard is visible but NOT operable: without this a keyboard user
  // tabs straight into the cockpit under the scrim and can start a second sync while setup's own
  // save is still writing. `inert` is set by ref because React 18 does not type the attribute.
  const cockpitRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = cockpitRef.current;
    if (!el) return;
    if (setup) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }, [setup]);

  // The skills check, asked once before the first account. The latch is what opens the ledger on
  // the TAP rather than a network round trip later: the write goes through React Query, so the
  // cached level still reads null until an invalidate-and-refetch has returned. It is also what
  // carries a FAILED write — the builder gets their mapping either way, and the next visit asks
  // again because nothing was recorded. Cleared when setup leaves the URL and nowhere else, so a
  // close-and-reopen in the same session does not re-ask.
  const { data: profile, isSuccess, isError } = useCurrentUser();
  const [skillAnswered, setSkillAnswered] = useState(false);
  useEffect(() => {
    if (!setup) setSkillAnswered(false);
  }, [setup]);

  const gate = setupGate({
    setupRequested: Boolean(setup),
    profileSettled: isSuccess || isError,
    level: profile?.finance_skill_level,
    answeredThisSession: skillAnswered,
  });

  // Once the ledger is up it STAYS up for this open. Without this, a profile read that failed
  // (→ 'ledger') and then succeeded on a later refetch with a null level would flip back to
  // 'ask', unmounting the overlay mid-answer. The staged answers survive that — they live on this
  // route for exactly that reason — but the overlay's own state does not. Setting a latch that is
  // already set is a no-op, so this settles on the first render that reaches the ledger.
  useEffect(() => {
    if (gate === 'ledger') setSkillAnswered(true);
  }, [gate]);

  /*
   * BACK TO THE DASHBOARD WITH THE FIGURES MOVED.
   *
   * Answers are written as they are made, but the figures are only recomputed when a chapter is
   * continued. A builder who follows a fix icon, answers the one question and closes setup never
   * presses Continue — so the figure they came to fix stayed grey. Setup tells us when it wrote
   * something; once it has closed AND every write has landed, the cheap recompute runs here,
   * on the route, which outlives the overlay (a write still in the air when it unmounts would
   * otherwise be recomputed before it existed).
   */
  const { data: orgData } = useCurrentOrganization();
  const sync = useSyncXeroProfitAndLoss(orgData?.organization?.id ?? null);
  const mappingsTouched = useRef(false);
  const writesInFlight = useIsMutating({ mutationKey: XERO_MAPPING_WRITE_KEY });
  useEffect(() => {
    if (setup || writesInFlight > 0 || !mappingsTouched.current) return;
    mappingsTouched.current = false;
    sync.mutate({ reason: 'mapping' }, {
      onError: () =>
        toast.warning("Your answers are saved, but the figures couldn't update — tap Refresh."),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup, writesInFlight]);

  if (!isXeroEnabled()) {
    return <PageStub title="Finance Hub" description="Financial management and insights" />;
  }
  return (
    <FinanceGuard>
      {/* The cockpit STAYS MOUNTED and live behind the overlay. That is the whole argument for
          putting setup here rather than on its own route: the builder watches their own figures
          move as each chapter clears, and movement is what teaches the model. */}
      <div ref={cockpitRef}>
        <FinancialCockpit
          askQuestion={ask ?? null}
          onAskChange={(id) =>
            navigate({ search: (prev) => ({ ...prev, ask: id ?? undefined }), replace: true })}
        />
      </div>
      {/* "How long after a month ends are your books usually closed?" — from The Gap's
          "still being closed off" popover. */}
      <AnimatePresence>
        {closeout && (
          <CloseOutDialog
            key="close-out"
            organizationId={orgData?.organization?.id ?? null}
            onClose={() => navigate({ search: ({ closeout: _c, ...rest }) => rest, replace: true })}
          />
        )}
      </AnimatePresence>
      {gate === 'ask' && (
        <FinanceSkillCheck open onAnswered={() => setSkillAnswered(true)} />
      )}
      {/* The ledger lives at the top right of the dashboard: it fades in, and on close shrinks back
          there (Tim, 12 Oct 2026) — the same exit as the key-question dialog. */}
      <AnimatePresence>
      {gate === 'ledger' && (
        <SetupOverlay
          key="ledger"
          chapterId={chapter ?? null}
          accountId={account ?? null}
          roleId={role ?? null}
          metricId={metric ?? null}
          answers={answers}
          setAnswers={setAnswers}
          skipped={skipped}
          setSkipped={setSkipped}
          onMappingsWritten={() => {
            mappingsTouched.current = true;
          }}
          onRecomputed={() => {
            mappingsTouched.current = false;
          }}
          // Choosing a chapter clears a one-shot account or figure link, which would otherwise
          // outrank the builder's own choice and pin them to the linked chapter.
          onChapterChange={(id, focusAccount) =>
            navigate({
              search: (prev) => ({ ...prev, chapter: id, account: focusAccount, role: undefined, metric: undefined }),
              replace: true,
            })}
          onClose={() =>
            navigate({
              search: ({ setup: _s, chapter: _c, account: _a, role: _r, metric: _m, ...rest }) => rest,
              replace: true,
            })}
        />
      )}
      </AnimatePresence>
    </FinanceGuard>
  );
}

export const Route = createFileRoute('/finance/')({
  // ?setup=1 opens the mapping overlay OVER this dashboard. ?chapter=<id> lands
  // on a chapter — the dashboard's "3 accounts placed roughly" links here, and a
  // link that dropped the builder at the top of setup would make them hunt.
  // ?account=<guid> lands on the chapter holding that account and highlights it (the "placed
  // roughly" marker, an unmapped account in the tie-out). ?role=<role> lands on the chapter that can
  // take that role and opens its "still waiting for" question, ringed — the fix icon on a figure
  // that can't calculate sends the one role it is missing. ?metric=<id> (no role known) lands on
  // the first chapter that can supply a role the figure needs.
  // Precedence when several are present: account, then role, then metric, then chapter.
  validateSearch: (search: Record<string, unknown>): {
    setup?: boolean;
    chapter?: string;
    account?: string;
    role?: string;
    metric?: string;
    /** A key question to open over the dashboard (lib/key-questions). */
    ask?: string;
    /** Open "how long after a month ends are your books usually closed?" (CloseOutDialog). */
    closeout?: boolean;
  } => ({
    // TanStack parses search values as JSON, so a typed `?setup=1` arrives as the NUMBER 1 and
    // `?setup=true` as the boolean. Accept every spelling a person or a link might use; rejecting
    // one silently strips it and the router rewrites the URL without it.
    setup: [true, 1, '1', 'true'].includes(search.setup as never) ? true : undefined,
    chapter: typeof search.chapter === 'string' ? search.chapter : undefined,
    account: typeof search.account === 'string' ? search.account : undefined,
    role: typeof search.role === 'string' ? search.role : undefined,
    ask: typeof search.ask === 'string' ? search.ask : undefined,
    closeout: [true, 1, '1', 'true'].includes(search.closeout as never) ? true : undefined,
    metric: typeof search.metric === 'string' ? search.metric : undefined,
  }),
  component: FinancePage,
});
